import { useState, useRef, useEffect } from "react";
import type { SseFlightMessage, FlightItem } from "@/api/aiChat";
import { INITIAL_SHOW_COUNT, LAZY_LOAD_STEP } from "./train-card";

// ════════════════════════════════════════════════════
//  机票/航班列表卡片（与 TrainCard 同风格 + 懒加载）
// ════════════════════════════════════════════════════

/** 从航班名提取航空公司简称 */
function extractAirline(flightName: string): string {
  const match = flightName.match(/^(.+?)[A-Z0-9]+$/);
  return match ? match[1] : flightName.split(/[A-Z]/)[0] || "";
}

/** 从航班名提取航班号 */
function extractFlightNo(flightName: string): string {
  // 匹配 航空公司代号+数字 格式，优先长匹配
  // 如 MU6644, 3U8822, CA1421, CZ6161, U8890, N5155 等
  const matches = flightName.match(/[A-Za-z]*\d+[A-Za-z]*/g);
  if (!matches || matches.length === 0) return flightName;
  // 返回最长/最可能的航班号（通常包含字母前缀的）
  return matches.reduce((longest, current) =>
    current.length > longest.length ? current : longest
  );
}

/** 航空公司颜色映射 */
const AIRLINE_COLOR: Record<string, { bg: string; color: string }> = {
  "中国国际航空": { bg: "#DBEAFE", color: "#1D4ED8" },
  "东方航空": { bg: "#FCE7F3", color: "#BE185D" },
  "南方航空": { bg: "#E0F2FE", color: "#0284C7" },
  "四川航空": { bg: "#FEF2F2", color: "#DC2626" },
  "海南航空": { bg: "#F0FDF4", color: "#16A34A" },
  "成都航空": { bg: "#FFF7ED", color: "#EA580C" },
};

interface FlightQueryRaw {
  departure: string;
  destination: string;
  date: string;
  total: number;
  flights: FlightItem[];
  is_round_trip?: boolean;
  return_total?: number;
  return_flights?: FlightItem[];
}

/** 单个航班列表区域（复用于去程和返程） */
function FlightListSection({
  title,
  subtitle,
  iconColor,
  flights,
  onFlightSelect,
}: {
  title: string;
  subtitle: string;
  iconColor: string;
  flights: FlightItem[];
  onFlightSelect?: (flight: FlightItem) => void;
}) {
  const [visibleCount, setVisibleCount] = useState(INITIAL_SHOW_COUNT);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const showFlights = flights.slice(0, visibleCount);
  const isAllLoaded = visibleCount >= flights.length;

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || isAllLoaded) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisibleCount((prev) => Math.min(prev + LAZY_LOAD_STEP, flights.length));
      },
      { rootMargin: "80px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isAllLoaded, flights.length]);

  return (
    <div>
      {/* 区段标题 */}
      <div className="flex items-center gap-2 px-3.5 py-2"
        style={{ background: "linear-gradient(135deg, #ECFEFF, #CFFAFE)", borderBottom: "1px solid #EEF0F6" }}
      >
        <i className="ri-plane-takeoff-line" style={{ color: iconColor, fontSize: 13 }} />
        <span className="text-[11px] font-semibold" style={{ color: "#333" }}>{title}</span>
        <span className="text-[10px]" style={{ color: "#94A3B8" }}>{subtitle}</span>
        {!isAllLoaded && (
          <span className="text-[9px] px-1.5 py-0.5 rounded ml-auto" style={{ background: "#CFFAFE", color: "#0891B2" }}>
            已显示 {visibleCount}/{flights.length}
          </span>
        )}
      </div>

      {/* 航班列表 */}
      <div className="flex flex-col">
        {showFlights.map((flight: FlightItem, idx: number) => {
          const airline = extractAirline(flight.name);
          const ac = AIRLINE_COLOR[airline] || { bg: "#F1F5F9", color: "#475569" };

          return (
            <div
              key={flight.name + idx}
              className={`px-3.5 py-3 ${onFlightSelect ? "cursor-pointer active:scale-[0.995]" : "active:bg-[#ECFEFF]"}`}
              style={{
                transition: "background 0.15s, transform 0.1s",
                borderBottom: idx < showFlights.length - 1 ? "1px solid #F0F2F8" : "none",
              }}
              onClick={() => onFlightSelect?.(flight)}
            >
              {/* 第一行：航班号 + 航司 */}
              <div className="flex items-center gap-1.5 mb-1">
                <i className="ri-plane-line flex-shrink-0" style={{ color: "#06B6D4", fontSize: 14 }} />
                <span className="text-[13px] font-bold flex-shrink-0" style={{ color: "#1E293B", fontFamily: "monospace" }}>
                  {extractFlightNo(flight.name)}
                </span>
                <i className="ri-arrow-right-s-line text-[8px] text-gray-300 flex-shrink-0 select-none" />
                <span
                  className="text-[9px] font-medium px-1.5 py-0.5 rounded flex-shrink-0"
                  style={{ background: ac.bg, color: ac.color }}
                >
                  {airline}
                </span>
              </div>

              {/* 第二行：机型 + 飞行时长 */}
              <div className="flex items-center justify-between gap-2 mb-1.5 text-[10px]" style={{ color: "#94A3B8" }}>
                <span>{flight.type}</span>
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded"
                  style={{ background: "#ECFEFF", color: "#0891B2" }}
                >
                  <i className="ri-time-line" style={{ fontSize: 10 }} /> {flight.times}
                </span>
              </div>

              {/* 第二行：时间 + 机场 | 服务标签 */}
              <div className="flex items-center justify-between gap-2 text-[11px]" style={{ color: "#94A3B8" }}>
                {/* 时间线 + 机场 */}
                <div className="flex items-center gap-x-2.5 flex-wrap flex-1 min-w-0">
                  {/* 时间线 */}
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <span className="font-semibold" style={{ color: "#06B6D4", fontFamily: "monospace", fontSize: 13 }}>
                      {flight.sarttime}
                    </span>
                    <i className="ri-arrow-right-line" style={{ fontSize: 10, color: "#CBD5E1" }} />
                    <span className="font-semibold" style={{ color: "#0891B2", fontFamily: "monospace", fontSize: 13 }}>
                      {flight.endtime}
                    </span>
                  </div>

                  {/* 分隔符 */}
                  <span className="text-[8px] text-gray-300 select-none hidden sm:inline">|</span>

                  {/* 机场 */}
                  <div className="flex items-center gap-1 text-[10px] flex-shrink-0" style={{ color: "#78716C" }}>
                    <span>{flight.startaddr}</span>
                    <i className="ri-arrow-right-line" style={{ fontSize: 8 }} />
                    <span>{flight.endaddr}</span>
                  </div>
                </div>

                {/* 服务标签 - 右侧固定位置 */}
                {flight.labels?.[0] && flight.labels[0].trim() && (
                  <div className="flex items-center gap-1 flex-shrink-0 ml-auto">
                    {flight.labels.map((label, i) =>
                      label.trim() && (
                        <span key={i} className="text-[9px] px-1.5 py-0.5 rounded whitespace-nowrap"
                          style={{ background: "#FEF3C7", color: "#B45309" }}
                        >
                          {label.trim()}
                        </span>
                      )
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* 懒加载哨兵 */}
        {!isAllLoaded && (
          <div ref={sentinelRef} className="flex items-center justify-center py-3">
            <div className="flex items-center gap-1.5 text-[10px]" style={{ color: "#AAA" }}>
              <i className="ri-loader-4-line animate-spin" style={{ fontSize: 12 }} />
              <span>向下滑动加载更多...</span>
            </div>
          </div>
        )}

        {/* 全部加载完毕 */}
        {isAllLoaded && flights.length > INITIAL_SHOW_COUNT && (
          <div className="flex items-center justify-center py-2" style={{ borderTop: "1px solid #F0F2F8" }}>
            <span className="text-[10px]" style={{ color: "#C0C8D4" }}>
              — 共 {flights.length} 趟航班 —
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function FlightCard({ data, onFlightSelect }: { data: SseFlightMessage | FlightQueryRaw; onFlightSelect?: (flight: FlightItem) => void }) {
  const fd = ("data" in data && (data as SseFlightMessage).data)
    ? (data as SseFlightMessage).data
    : data as FlightQueryRaw;

  if (!fd?.flights?.length) return null;

  const hasReturn = fd.is_round_trip && fd.return_flights?.length > 0;

  return (
    <div className="flex flex-col gap-0 rounded-xl w-full"
      style={{ background: "#FAFBFF", border: "1px solid #EEF0F6" }}
    >
      {/* 总标题栏 */}
      <div
        className="flex items-center gap-2 px-3.5 py-2.5 flex-wrap"
        style={{ background: "linear-gradient(135deg, #ECFEFF, #CFFAFE)", borderBottom: "1px solid #EEF0F6" }}
      >
        <i className="ri-flight-takeoff-line" style={{ color: "#06B6D4", fontSize: 15 }} />
        <span className="text-[12px] font-semibold" style={{ color: "#333" }}>
          {fd.departure} ✈ {fd.destination}
        </span>
        <span className="text-[10px] px-2 py-0.5 rounded-full font-mono"
          style={{ background: "#CFFAFE", color: "#0891B2" }}
        >
          {fd.date.split(" ")[0]}
        </span>
        {hasReturn && (
          <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: "#DBEAFE", color: "#1D4ED8" }}>
            往返
          </span>
        )}
        <span className="text-[10px]" style={{ color: "#999" }}>
          共{fd.total}趟{hasReturn ? ` / 返${fd.return_total}` : ""}
        </span>
      </div>

      {/* 去程航班 */}
      <FlightListSection
        title="去程"
        subtitle={`${fd.total} 趟可选`}
        iconColor="#06B6D4"
        flights={fd.flights}
        onFlightSelect={onFlightSelect}
      />

      {/* 返程航班（如果有） */}
      {hasReturn && fd.return_flights && (
        <div style={{ borderTop: "2px dashed #E0F2FE" }}>
          <FlightListSection
            title="返程"
            subtitle={`${fd.return_total} 趟可选`}
            iconColor="#0284C7"
            flights={fd.return_flights!}
            onFlightSelect={onFlightSelect}
          />
        </div>
      )}
    </div>
  );
}
